import { useEffect, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import type { DocNode } from "@edm/schema";
import { api } from "../../api/client";

interface PaletteItem {
    blockType: string;
    label: string;
    icon: string;
}

export interface SavedBlockItem {
    id: string;
    name: string;
    category: string;
    scope: string;
    block: DocNode;
}

const LAYOUT_ITEMS: PaletteItem[] = [
    { blockType: "section", label: "Section", icon: "▭" },
    { blockType: "section:1-1", label: "2 columns", icon: "◫" },
    { blockType: "section:1-1-1", label: "3 columns", icon: "◫◫" },
    { blockType: "section:1-2", label: "1 : 2", icon: "◧" },
    { blockType: "hero", label: "Hero", icon: "★" },
    { blockType: "card", label: "Card", icon: "▢" },
];

const CONTENT_ITEMS: PaletteItem[] = [
    { blockType: "heading", label: "Heading", icon: "H" },
    { blockType: "paragraph", label: "Text", icon: "¶" },
    { blockType: "list", label: "List", icon: "≡" },
    { blockType: "image", label: "Image", icon: "🖼" },
    { blockType: "logo", label: "Logo", icon: "◉" },
    { blockType: "button", label: "Button", icon: "▢" },
    { blockType: "divider", label: "Divider", icon: "—" },
    { blockType: "spacer", label: "Spacer", icon: "↕" },
    { blockType: "social", label: "Social", icon: "@" },
    { blockType: "nav", label: "Menu", icon: "☰" },
    { blockType: "callout", label: "Callout", icon: "▣" },
    { blockType: "quote", label: "Quote", icon: "❝" },
    { blockType: "footer", label: "Footer", icon: "⌂" },
    { blockType: "rawHtml", label: "HTML", icon: "</>" },
];

const ALL_ITEMS = [...LAYOUT_ITEMS, ...CONTENT_ITEMS];

export function paletteLabel(blockType: string): string {
    return ALL_ITEMS.find((i) => i.blockType === blockType)?.label ?? blockType;
}

export function BlockPalette({ savedBlocksVersion }: { savedBlocksVersion: number }) {
    const [tab, setTab] = useState<"blocks" | "saved">("blocks");
    const [saved, setSaved] = useState<SavedBlockItem[]>([]);

    useEffect(() => {
        api.get<SavedBlockItem[]>("/api/v1/blocks")
            .then(setSaved)
            .catch(() => setSaved([]));
    }, [savedBlocksVersion]);

    return (
        <aside className="palette" aria-label="Block palette">
            <div className="insp-tabs">
                <button
                    type="button"
                    className={`insp-tab${tab === "blocks" ? " active" : ""}`}
                    onClick={() => setTab("blocks")}
                >
                    Blocks
                </button>
                <button
                    type="button"
                    className={`insp-tab${tab === "saved" ? " active" : ""}`}
                    onClick={() => setTab("saved")}
                    data-testid="saved-blocks-tab"
                >
                    Saved
                </button>
            </div>
            {tab === "blocks" ? (
                <>
                    <div className="palette-group">
                        <div className="palette-title">Layout</div>
                        {LAYOUT_ITEMS.map((item) => (
                            <PaletteEntry key={item.blockType} item={item} />
                        ))}
                    </div>
                    <div className="palette-group">
                        <div className="palette-title">Content</div>
                        {CONTENT_ITEMS.map((item) => (
                            <PaletteEntry key={item.blockType} item={item} />
                        ))}
                    </div>
                </>
            ) : (
                <div className="palette-group">
                    {saved.length === 0 && (
                        <p className="insp-hint">
                            Select a block on the canvas and use the ★ button to save it for reuse.
                        </p>
                    )}
                    {saved.map((b) => (
                        <SavedEntry key={b.id} item={b} />
                    ))}
                </div>
            )}
        </aside>
    );
}

function SavedEntry({ item }: { item: SavedBlockItem }) {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: `saved:${item.id}`,
        data: { kind: "saved", block: item.block, label: item.name },
    });
    return (
        <button
            ref={setNodeRef}
            type="button"
            className="palette-item"
            data-testid={`saved-block-${item.name.replace(/\W+/g, "-")}`}
            {...attributes}
            {...listeners}
        >
            <span className="palette-icon" aria-hidden>
                ★
            </span>
            {item.name}
        </button>
    );
}

function PaletteEntry({ item }: { item: PaletteItem }) {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: `palette:${item.blockType}`,
        data: { kind: "palette", blockType: item.blockType },
    });
    return (
        <button
            ref={setNodeRef}
            type="button"
            className="palette-item"
            data-testid={`palette-${item.blockType}`}
            {...attributes}
            {...listeners}
        >
            <span className="palette-icon" aria-hidden>
                {item.icon}
            </span>
            {item.label}
        </button>
    );
}
